import { useCallback } from "react";
import { trpc, type RouterInputs } from "../trpc";
import {
  getPrivateFolderOwnerMismatch,
  type PrivateFolderOwnerMismatch,
} from "./privateFolderOwnerMismatch";

export type PrivateFolderOwnerPolicy = NonNullable<
  RouterInputs["asset"]["moveToFolder"]["privateFolderOwnerPolicy"]
>;

export interface BulkMoveResult {
  totalRequested: number;
  movedCount: number;
  failedCount: number;
  /** Set when the asset batch was refused under the "refuse" policy. Those assets were neither moved nor failed. */
  ownerMismatch?: PrivateFolderOwnerMismatch & { assetIds: string[] };
}

interface Input {
  folderId?: string | null;
}

interface BulkMoveOptions {
  privateFolderOwnerPolicy?: PrivateFolderOwnerPolicy;
}

/**
 * Moves a mixed set of typed asset IDs (e.g. "zap:123", "folder:abc") to a
 * destination folder. Non-folder assets go through `asset.moveToFolder`;
 * folders go through `folder.moveFolders`.
 *
 * **Known limitation:** `movedCount` / `failedCount` are batch-granular, not
 * per-asset. If `asset.moveToFolder` partially succeeds server-side (e.g.
 * moves 3 of 5), the entire batch is still counted as fulfilled. The toast
 * may therefore over-report successes. This will remain until the BFF returns
 * per-asset results from the Org API.
 */
export function useBulkMoveAssets(input?: Input) {
  const { folderId } = input || {};
  const utils = trpc.useUtils();

  const moveAssets = trpc.asset.moveToFolder.useMutation({
    onSettled: () => {
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({ folderId });
      }
      utils.zap.organizationZaps.invalidate();
      utils.zap.storageZaps.invalidate();
      utils.folder.foldersLookup.invalidate();
      utils.folder.listAssetsInFolderSubtree.invalidate();
    },
  });

  const moveFolders = trpc.folder.moveFolders.useMutation({
    onSettled: () => {
      utils.folder.folderTree.invalidate();
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({ folderId });
      }
      utils.folder.listAssetsInFolderSubtree.invalidate();
      utils.folder.listFolderSubtreeMaxDepth.invalidate();
    },
  });

  const bulkMoveAssets = useCallback(
    async (
      typedAssetIds: string[],
      destinationFolderId: string,
      { privateFolderOwnerPolicy }: BulkMoveOptions = {},
    ): Promise<BulkMoveResult> => {
      const folderIds: string[] = [];
      const assetIds: string[] = [];

      for (const id of typedAssetIds) {
        const colonIndex = id.indexOf(":");
        const type = id.slice(0, colonIndex);
        const rawId = id.slice(colonIndex + 1);
        if (type === "folder") {
          folderIds.push(rawId);
        } else {
          assetIds.push(id);
        }
      }

      const results = await Promise.allSettled([
        assetIds.length > 0
          ? moveAssets.mutateAsync({
              assetIds,
              destinationFolderId,
              ...(privateFolderOwnerPolicy ? { privateFolderOwnerPolicy } : {}),
            })
          : Promise.resolve(),
        folderIds.length > 0
          ? moveFolders.mutateAsync({
              folderIds,
              parentId: destinationFolderId,
            })
          : Promise.resolve(),
      ]);

      const assetCount = assetIds.length;
      const folderCount = folderIds.length;
      let movedCount = 0;
      let failedCount = 0;
      let ownerMismatch: BulkMoveResult["ownerMismatch"];

      if (results[0].status === "fulfilled") {
        movedCount += assetCount;
      } else {
        const mismatch = getPrivateFolderOwnerMismatch(results[0].reason);
        if (mismatch) ownerMismatch = { ...mismatch, assetIds };
        else failedCount += assetCount;
      }

      if (results[1].status === "fulfilled") movedCount += folderCount;
      else failedCount += folderCount;

      return {
        totalRequested: typedAssetIds.length,
        movedCount,
        failedCount,
        ...(ownerMismatch ? { ownerMismatch } : {}),
      };
    },
    [moveAssets, moveFolders],
  );

  return {
    bulkMoveAssets,
    isPending: moveAssets.isPending || moveFolders.isPending,
  };
}
